import { Component, OnInit, inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { first, Observable, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';
import { NgbTypeaheadSelectItemEvent } from '@ng-bootstrap/ng-bootstrap';
import { AiOrderParserConfig } from '../../../models/ai-order-parser-config';
import { AiOrderParserWarning } from '../../../models/ai-order-parser-warning';
import { PdfAiParserService, ParsedOrder, ParsedOrderRow } from '../../../services/pdf-ai-parser/pdf-ai-parser.service';
import {
  AiOrderParserReviewService,
  isSupportedAiParserFile
} from '../../../services/ai-order-parser-review/ai-order-parser-review.service';
import { Customer } from '../../../models/customer';
import { CustomerService } from '../../../services/customers/customer.service';
import { UsersService } from '../../../services/users/users.service';
import { CanDeactivateComponent } from '../../can-deactivate.component';

@Component({
  selector: 'lib-pdf-ai-parser-config-edit',
  standalone: false,
  templateUrl: './pdf-ai-parser-config-edit.component.html',
  styleUrl: './pdf-ai-parser-config-edit.component.scss'
})
export class PdfAiParserConfigEditComponent extends CanDeactivateComponent implements OnInit {
  isNew = true;
  loading = false;
  /** Load of an existing config failed: no form, so a save can't overwrite it with blanks. */
  loadFailed = false;
  saving = false;

  /** Persistence id of the config being edited; undefined for a new config. */
  id?: string;
  name = '';
  aiInstructions = '';
  senderEmails: string[] = [];
  responsibleUsername = '';
  autoSubmitEnabled = false;
  /** Server-level master switch, read-only here: a config opt-in does nothing while this is off. */
  serverAutoSubmitEnabled = false;
  deliveryCustomerId = '';
  billingCustomerId = '';
  aiDecidesDeliveryLocation = false;
  /** Round-tripped so an update doesn't reset it (the backend defaults it to now). */
  private createdDate?: string;

  /** Active users, for the responsible-user picker. Empty when the lookup failed or is not permitted. */
  users: { username: string; name?: string }[] = [];

  newEmail = '';
  nameError = '';
  errorMessage = '';

  deliveryCustomerName: string | null = null;
  billingCustomerName: string | null = null;
  resolvingDelivery = false;
  resolvingBilling = false;
  private customerNameCache = new Map<string, string>();

  // AI-assisted instruction generator + test. All transient — the sample and description are
  // never persisted and are deliberately kept out of the dirty-check snapshot().
  /** The generator is opt-in: collapsed by default so it never crowds the core config fields. */
  assistOpen = false;
  sampleFile: File | null = null;
  sampleError = '';
  description = '';
  generating = false;
  generateError = '';
  testing = false;
  testError = '';
  testResult: ParsedOrder | null = null;
  testWarnings: AiOrderParserWarning[] = [];

  /** File types accepted by the transient sample picker; mirrors the backend accept list. */
  readonly sampleAccept = '.pdf,.xlsx,.xls,.csv,.png,.jpg,.jpeg,.gif,.webp';

  /** JSON snapshot of the last-persisted (or freshly-loaded) state, for the dirty check. */
  private savedSnapshot = '';

  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private pdfAiParserService = inject(PdfAiParserService);
  private customerService = inject(CustomerService);
  private reviewService = inject(AiOrderParserReviewService);
  private usersService = inject(UsersService);
  private translateService = inject(TranslateService);

  ngOnInit(): void {
    this.loadUsers();
    this.loadServerAutoSubmit();
    const routeId = this.route.snapshot.paramMap.get('id');
    if (routeId && routeId !== 'new') {
      this.isNew = false;
      this.id = routeId;
      this.loadConfig(routeId);
    } else {
      this.savedSnapshot = this.snapshot();
    }
  }

  /**
   * Only active users can be chosen: the backend refuses to save an inactive one, since an account
   * nobody uses cannot stand behind an automatically submitted order. A failed lookup leaves the
   * list empty rather than blocking config editing, which works without a responsible user.
   */
  private loadUsers(): void {
    this.usersService.getAllUsers().pipe(first()).subscribe({
      next: users => {
        this.users = (users || [])
          .map(u => u as unknown as { username: string; name?: string; active?: boolean })
          .filter(u => !!u.username && u.active !== false)
          .sort((a, b) => (a.name || a.username).localeCompare(b.name || b.username));
      },
      error: () => {
        this.users = [];
      }
    });
  }

  /**
   * The stored responsible user is not among the loaded active users (deactivated or deleted), so the
   * backend would refuse the save. False until the users have loaded, so nothing is flagged early.
   */
  get responsibleUserInactive(): boolean {
    const username = this.responsibleUsername.trim();
    return !!username && this.users.length > 0 && !this.users.some(u => u.username === username);
  }

  /** Only to tell the admin whether this config's opt-in is currently live; never written back. */
  private loadServerAutoSubmit(): void {
    this.pdfAiParserService.getSettings().pipe(first()).subscribe({
      next: settings => (this.serverAutoSubmitEnabled = !!settings?.autoSubmitEnabled),
      error: () => (this.serverAutoSubmitEnabled = false)
    });
  }

  private loadConfig(id: string): void {
    this.loading = true;
    this.pdfAiParserService.getConfig(id).pipe(first()).subscribe({
      next: config => {
        this.applyConfig(config);
        this.loading = false;
      },
      error: () => {
        this.errorMessage = 'AI_PARSER_ERROR_LOAD_CONFIGS';
        this.loadFailed = true;
        this.loading = false;
      }
    });
  }

  private applyConfig(config: AiOrderParserConfig): void {
    this.id = config.id;
    this.name = config.name || '';
    this.aiInstructions = config.aiInstructions || '';
    this.senderEmails = config.senderEmails ? [...config.senderEmails] : [];
    this.responsibleUsername = config.responsibleUsername || '';
    this.autoSubmitEnabled = !!config.autoSubmitEnabled;
    this.deliveryCustomerId = config.suggestedDeliveryCustomerId || '';
    this.billingCustomerId = config.suggestedBillingCustomerId || '';
    this.aiDecidesDeliveryLocation = !!config.aiDecidesDeliveryLocation;
    this.createdDate = config.createdDate;
    this.savedSnapshot = this.snapshot();
    this.resolveCustomerName('delivery');
    this.resolveCustomerName('billing');
  }

  addEmail(): void {
    const trimmed = this.newEmail.trim().toLowerCase();
    if (trimmed && !this.senderEmails.includes(trimmed)) {
      this.senderEmails = [...this.senderEmails, trimmed];
    }
    this.newEmail = '';
  }

  removeEmail(email: string): void {
    this.senderEmails = this.senderEmails.filter(e => e !== email);
  }

  onCustomerIdChange(field: 'delivery' | 'billing'): void {
    this.resolveCustomerName(field);
  }

  /** Same name-or-id customer picker as the review page; the field still holds the id. */
  searchCustomersByName = (text$: Observable<string>): Observable<Customer[]> => {
    return text$.pipe(
      debounceTime(200),
      distinctUntilChanged(),
      switchMap(term =>
        term.trim().length < 2
          ? of([])
          : this.reviewService.searchCustomers(term.trim()).pipe(catchError(() => of([])))
      )
    );
  };

  /** Dropdown row: both attributes, since the search matches either one. */
  customerSearchFormatter(result: Customer): string {
    return result.id + ' — ' + result.name;
  }

  /** What lands in the input once a hit is picked: the id, never the whole formatted row. */
  customerIdFormatter = (result: Customer | string): string =>
    typeof result === 'string' ? result : result?.id || '';

  /** preventDefault because ngbTypeahead would otherwise write the whole Customer into the field. */
  onCustomerPicked(field: 'delivery' | 'billing', event: NgbTypeaheadSelectItemEvent<Customer>): void {
    event.preventDefault();
    if (field === 'delivery') {
      this.deliveryCustomerId = event.item.id;
    } else {
      this.billingCustomerId = event.item.id;
    }
    this.resolveCustomerName(field);
  }

  private resolveCustomerName(field: 'delivery' | 'billing'): void {
    const customerId = (field === 'delivery' ? this.deliveryCustomerId : this.billingCustomerId).trim();
    const assign = (name: string | null, resolving: boolean) => {
      if (field === 'delivery') {
        this.deliveryCustomerName = name;
        this.resolvingDelivery = resolving;
      } else {
        this.billingCustomerName = name;
        this.resolvingBilling = resolving;
      }
    };
    if (!customerId) {
      assign(null, false);
      return;
    }
    if (this.customerNameCache.has(customerId)) {
      assign(this.customerNameCache.get(customerId)!, false);
      return;
    }
    assign(null, true);
    this.customerService.getCustomer(customerId).pipe(first()).subscribe({
      next: customer => {
        const name = customer?.name || '';
        this.customerNameCache.set(customerId, name);
        assign(name, false);
      },
      error: () => {
        this.customerNameCache.set(customerId, '');
        assign('', false);
      }
    });
  }

  save(): void {
    if (this.loadFailed) {
      return;
    }
    this.nameError = '';
    this.errorMessage = '';
    const trimmedName = this.name.trim();
    if (!trimmedName) {
      this.nameError = 'AI_PARSER_CONFIG_NAME_REQUIRED';
      return;
    }
    if (this.responsibleUserInactive) {
      this.errorMessage = 'AI_PARSER_RESPONSIBLE_USER_INACTIVE';
      return;
    }
    const payload: AiOrderParserConfig = {
      id: this.id,
      createdDate: this.createdDate,
      name: trimmedName,
      aiInstructions: this.aiInstructions.trim() || undefined,
      senderEmails: this.senderEmails.length ? this.senderEmails : undefined,
      responsibleUsername: this.responsibleUsername.trim() || undefined,
      autoSubmitEnabled: this.autoSubmitEnabled,
      suggestedDeliveryCustomerId: this.deliveryCustomerId.trim() || undefined,
      suggestedBillingCustomerId: this.billingCustomerId.trim() || undefined,
      aiDecidesDeliveryLocation: this.aiDecidesDeliveryLocation
    };
    this.saving = true;
    this.pdfAiParserService.saveConfig(payload).pipe(first()).subscribe({
      next: saved => {
        this.applyConfig(saved);
        this.saving = false;
        this.router.navigate(['/order-parser/configs']);
      },
      error: (err: HttpErrorResponse) => {
        this.saving = false;
        if (err.status === 409) {
          this.nameError = 'AI_PARSER_CONFIG_NAME_CONFLICT';
        } else if (err.status === 400 && /user/i.test(String(err.error?.message || ''))) {
          // requireActiveResponsibleUser; only readable when the backend exposes the reason.
          this.errorMessage = 'AI_PARSER_RESPONSIBLE_USER_INACTIVE';
        } else {
          this.errorMessage = 'AI_PARSER_ERROR_SAVE_CONFIG';
        }
      }
    });
  }

  onSampleSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files && input.files.length ? input.files[0] : null;
    // Reset the input so re-picking the same file still fires (change) again.
    input.value = '';
    if (!file) {
      return;
    }
    if (!isSupportedAiParserFile(file)) {
      this.sampleError = 'AI_PARSER_CONFIG_ASSIST_UNSUPPORTED';
      return;
    }
    this.sampleError = '';
    this.generateError = '';
    this.testError = '';
    this.testResult = null;
    this.testWarnings = [];
    this.sampleFile = file;
  }

  clearSample(): void {
    this.sampleFile = null;
    this.sampleError = '';
    this.generateError = '';
    this.testError = '';
    this.testResult = null;
    this.testWarnings = [];
  }

  generate(): void {
    if (!this.sampleFile || this.generating) {
      return;
    }
    if (this.aiInstructions.trim()
        && !window.confirm(this.translateService.instant('AI_PARSER_CONFIG_OVERWRITE_WARNING'))) {
      return;
    }
    this.generating = true;
    this.generateError = '';
    this.pdfAiParserService.generateInstructions(this.sampleFile, this.description).pipe(first()).subscribe({
      next: result => {
        this.generating = false;
        const suggestion = (result?.aiInstructions || '').trim();
        if (suggestion) {
          this.aiInstructions = suggestion;
        } else {
          this.generateError = 'AI_PARSER_CONFIG_ASSIST_GENERATE_EMPTY';
        }
      },
      error: () => {
        this.generating = false;
        this.generateError = 'AI_PARSER_CONFIG_ASSIST_GENERATE_ERROR';
      }
    });
  }

  runTest(): void {
    if (!this.sampleFile || !this.aiInstructions.trim() || this.testing) {
      return;
    }
    this.testing = true;
    this.testError = '';
    this.testResult = null;
    this.testWarnings = [];
    const draft: AiOrderParserConfig = {
      name: this.name.trim() || 'test',
      aiInstructions: this.aiInstructions.trim() || undefined,
      suggestedDeliveryCustomerId: this.deliveryCustomerId.trim() || undefined,
      suggestedBillingCustomerId: this.billingCustomerId.trim() || undefined
    };
    this.pdfAiParserService.parseOrder({ file: this.sampleFile, config: draft }).pipe(first()).subscribe({
      next: response => {
        this.testing = false;
        if (response?.success && response.order) {
          this.testResult = response.order;
          this.testWarnings = response.warnings || [];
        } else {
          this.testError = 'AI_PARSER_CONFIG_ASSIST_TEST_ERROR';
        }
      },
      error: () => {
        this.testing = false;
        this.testError = 'AI_PARSER_CONFIG_ASSIST_TEST_ERROR';
      }
    });
  }

  get testOrderNumber(): string | undefined {
    const value = this.testResult?.extra?.['customerOrderNumber'];
    return value != null ? String(value) : undefined;
  }

  get testDeliveryDate(): string | number | undefined {
    const gme = this.testResult?.extra?.['gme'] as { deliveryDate?: string | number } | undefined;
    return gme?.deliveryDate;
  }

  rowUnitPrice(row: ParsedOrderRow): string | number | undefined {
    const aiParser = row.extra?.['aiParser'] as { documentUnitPrice?: string | number } | undefined;
    return aiParser?.documentUnitPrice;
  }

  cancel(): void {
    this.router.navigate(['/order-parser/configs']);
  }

  canDeactivate(): boolean {
    return this.saving || this.snapshot() === this.savedSnapshot;
  }

  private snapshot(): string {
    return JSON.stringify({
      name: this.name.trim(),
      aiInstructions: this.aiInstructions.trim(),
      senderEmails: this.senderEmails,
      autoSubmitEnabled: this.autoSubmitEnabled,
      deliveryCustomerId: this.deliveryCustomerId.trim(),
      billingCustomerId: this.billingCustomerId.trim(),
      aiDecidesDeliveryLocation: this.aiDecidesDeliveryLocation
    });
  }
}
